/* Copyright (c) Trimble Inc. All rights reserved. */

/**
 * Logger utility that conditionally logs based on environment
 * Only logs when NODE_ENV is not 'production'
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */

type LogLevel =
  | 'log'
  | 'debug'
  | 'info'
  | 'warn'
  | 'error'
  | 'trace'
  | 'table'
  | 'group'
  | 'groupCollapsed'
  | 'groupEnd';

interface LoggerConfig {
  enabled?: boolean;
  prefix?: string;
  showTimestamp?: boolean;
}

class CustomLogger {
  private config: LoggerConfig;
  private isEnabled: boolean;

  constructor(config: LoggerConfig = {}) {
    this.config = {
      enabled: process.env.NODE_ENV !== 'production',
      prefix: '',
      showTimestamp: false,
      ...config,
    };

    this.isEnabled = this.config.enabled ?? false;
  }

  /**
   * Configure the logger
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.isEnabled = this.config.enabled ?? false;
  }

  /**
   * Get formatted prefix with optional timestamp
   */
  private getPrefix(): string {
    const parts: string[] = [];

    if (this.config.showTimestamp) {
      const timestamp = new Date().toISOString();
      parts.push(`[${timestamp}]`);
    }

    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    return parts.length > 0 ? `${parts.join(' ')} ` : '';
  }

  /**
   * Generic logging method
   */
  private logWithLevel(level: LogLevel, ...args: any[]): void {
    if (!this.isEnabled) return;

    const prefix = this.getPrefix();

    if (prefix && args.length > 0 && typeof args[0] === 'string') {
      // Add prefix to first string argument
      args[0] = prefix + args[0];
    } else if (prefix) {
      // Add prefix as first argument
      args.unshift(prefix.trim());
    }

    // Use the appropriate console method
    const consoleMethod = console[level] as (...args: any[]) => void;
    if (consoleMethod) {
      consoleMethod(...args);
    } else {
      // Fallback to console.log for unsupported methods
      console.log(...args);
    }
  }

  /**
   * Standard console.log
   */
  log(...args: any[]): void {
    this.logWithLevel('log', ...args);
  }

  /**
   * Debug logging - typically filtered out in production
   */
  debug(...args: any[]): void {
    this.logWithLevel('debug', ...args);
  }

  /**
   * Info logging
   */
  info(...args: any[]): void {
    this.logWithLevel('info', ...args);
  }

  /**
   * Warning logging
   */
  warn(...args: any[]): void {
    this.logWithLevel('warn', ...args);
  }

  /**
   * Error logging
   */
  error(...args: any[]): void {
    this.logWithLevel('error', ...args);
  }

  /**
   * Trace logging - shows stack trace
   */
  trace(...args: any[]): void {
    this.logWithLevel('trace', ...args);
  }

  /**
   * Table logging - displays data in tabular format
   */
  table(tabularData: any, properties?: string[]): void {
    if (!this.isEnabled) return;

    const prefix = this.getPrefix();
    if (prefix) {
      console.log(prefix.trim());
    }

    if (console.table) {
      console.table(tabularData, properties);
    } else {
      // Fallback for environments without console.table
      console.log('Table data:', tabularData);
    }
  }

  /**
   * Group logging - creates a collapsible group
   */
  group(label?: string): void {
    if (!this.isEnabled) return;

    const prefix = this.getPrefix();
    const fullLabel = prefix + (label || 'Group');

    if (console.group) {
      console.group(fullLabel);
    } else {
      console.log(`--- ${fullLabel} ---`);
    }
  }

  /**
   * Collapsed group logging
   */
  groupCollapsed(label?: string): void {
    if (!this.isEnabled) return;

    const prefix = this.getPrefix();
    const fullLabel = prefix + (label || 'Group');

    if (console.groupCollapsed) {
      console.groupCollapsed(fullLabel);
    } else {
      console.log(`--- ${fullLabel} (collapsed) ---`);
    }
  }

  /**
   * End group logging
   */
  groupEnd(): void {
    if (!this.isEnabled) return;

    if (console.groupEnd) {
      console.groupEnd();
    } else {
      console.log('--- End Group ---');
    }
  }

  /**
   * Conditional logging - only logs if condition is true
   */
  assert(condition: any, ...args: any[]): void {
    if (!this.isEnabled) return;

    if (console.assert) {
      const prefix = this.getPrefix();
      if (prefix && args.length > 0 && typeof args[0] === 'string') {
        args[0] = prefix + args[0];
      } else if (prefix) {
        args.unshift(prefix.trim());
      }
      console.assert(condition, ...args);
    } else {
      // Fallback implementation
      if (!condition) {
        this.error('Assertion failed:', ...args);
      }
    }
  }

  /**
   * Performance timing start
   */
  time(label = 'default'): void {
    if (!this.isEnabled) return;

    const fullLabel = this.getPrefix() + label;

    if (console.time) {
      console.time(fullLabel);
    } else {
      // Fallback - store start time
      (this as any)[`_time_${label}`] = Date.now();
    }
  }

  /**
   * Performance timing end
   */
  timeEnd(label = 'default'): void {
    if (!this.isEnabled) return;

    const fullLabel = this.getPrefix() + label;

    if (console.timeEnd) {
      console.timeEnd(fullLabel);
    } else {
      // Fallback implementation
      const startTime = (this as any)[`_time_${label}`];
      if (startTime) {
        const duration = Date.now() - startTime;
        this.log(`${fullLabel}: ${duration}ms`);
        delete (this as any)[`_time_${label}`];
      }
    }
  }

  /**
   * Performance timing log (doesn't end the timer)
   */
  timeLog(label = 'default', ...args: any[]): void {
    if (!this.isEnabled) return;

    const fullLabel = this.getPrefix() + label;

    if (console.timeLog) {
      console.timeLog(fullLabel, ...args);
    } else {
      // Fallback implementation
      const startTime = (this as any)[`_time_${label}`];
      if (startTime) {
        const duration = Date.now() - startTime;
        this.log(`${fullLabel}: ${duration}ms`, ...args);
      }
    }
  }

  /**
   * Clear console (if supported)
   */
  clear(): void {
    if (!this.isEnabled) return;

    if (console.clear) {
      console.clear();
    }
  }

  /**
   * Count logging - maintains a counter for the label
   */
  count(label = 'default'): void {
    if (!this.isEnabled) return;

    const fullLabel = this.getPrefix() + label;

    if (console.count) {
      console.count(fullLabel);
    } else {
      // Fallback implementation
      const countKey = `_count_${label}`;
      const currentCount = ((this as any)[countKey] || 0) + 1;
      (this as any)[countKey] = currentCount;
      this.log(`${fullLabel}: ${currentCount}`);
    }
  }

  /**
   * Reset count for label
   */
  countReset(label = 'default'): void {
    if (!this.isEnabled) return;

    const fullLabel = this.getPrefix() + label;

    if (console.countReset) {
      console.countReset(fullLabel);
    } else {
      // Fallback implementation
      delete (this as any)[`_count_${label}`];
    }
  }

  /**
   * Create a scoped logger with a prefix
   */
  createScope(prefix: string): CustomLogger {
    const scopedConfig = {
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}:${prefix}` : prefix,
    };

    return new CustomLogger(scopedConfig);
  }

  /**
   * Check if logging is enabled
   */
  get enabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Enable or disable logging
   */
  set enabled(value: boolean) {
    this.isEnabled = value;
    this.config.enabled = value;
  }
}

// Create default logger instance
export const Logger = new CustomLogger();

// Export the class for custom instances
export { CustomLogger };

// Export types
export type { LoggerConfig, LogLevel };

// Default export
export default Logger;
